import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Fab, Grid } from '@mui/material';
import Collapse from '@mui/material/Collapse';
import { PageSection } from '@skoda/k2tools-app-components';
import * as queryString from 'query-string';
import * as React from 'react';
import { StaticContext } from 'react-router';
import { RouteComponentProps } from 'react-router-dom';
import { SearchForm } from '../../components/search-form';
import { VinSearch } from '../../components/vin-search';
import { OwnersManualsAppContext } from '../../contexts/OwnersManualsContext';
import { VinAppContext } from '../../contexts/VinContext';
import { ErrorCode } from '../../enums/ErrorCode';
import { ManualPdfType } from '../../enums/manualPdfType';
import { ManualType } from '../../enums/manualType';
import { SearchError } from '../../enums/searchError';
import { ManualModel } from '../../models';
import { IUrlParameters } from '../../models/urlParameters';
import { ResetScroll } from '../../utils/scrollUtils';
import styles from './styles.scss';

type DetailPageProps = RouteComponentProps<{}, StaticContext, object>;

type DetailPageState = {
	isSearchOpen: boolean;
};

class DetailPage extends React.Component<DetailPageProps, DetailPageState> {
	static contextType = OwnersManualsAppContext;
	context: React.ContextType<typeof OwnersManualsAppContext>;

	constructor(props: DetailPageProps) {
		super(props);
		this.state = {
			isSearchOpen: false,
		};
	}

	componentDidMount() {
		window.scrollTo(0, 0);
		setTimeout(() => {
			ResetScroll();
		}, 1500);

		this.context.onError(this.handleError);

		const parseParams = queryString.parse(this.props.location.search);
		const params = parseParams as object as IUrlParameters;

		if (!this.context.model) {
			this.context.initData(params, (carModel) => {
				if (carModel) {
					this.context.services.trackingPrepare.trackDetailView(carModel.modelFamily, carModel.modelType);
					this.context.validate(params);
				}
			});
		} else {
			this.context.services.trackingPrepare.trackDetailView(this.context.model.modelFamily, this.context.model.modelType);
		}
	}

	componentWillUnmount() {
		if (this.context.model && this.context.model.isFromVin) {
			this.context.setModel(null);
		}
	}

	handleError = (searchError: SearchError) => {
		if (searchError === SearchError.ModelNotFound) {
			return this.props.history.replace('/Models', {
				isCurrentModel: true,
				isScrolledToVin: false,
				isScrolledToFamily: false,
			});
		}

		// When there is model or unrecognized VIN during page from from URL params
		if (
			this.context.hasError &&
			((this.context.urlParameters && this.context.urlParameters.vin && this.context.errorCode === SearchError.UnrecoginzedVin) ||
				(this.context.urlParameters.model && !this.context.model))
		) {
			return this.props.history.replace('/Models', {
				isCurrentModel: true,
				isScrolledToVin: this.context.errorCode === SearchError.UnrecoginzedVin,
				isScrolledToFamily: this.context.errorCode !== SearchError.UnrecoginzedVin && this.context.urlParameters.model && !this.context.model,
			});
		}

		if (searchError === SearchError.ParametersMismatch || searchError === SearchError.NoManuals) {
			this.setState({
				isSearchOpen: true,
			});
		}
	};

	handleCarSelectorError = (searchError: SearchError) => {
		if (
			searchError === SearchError.EditionNotFound ||
			searchError === SearchError.MarketNotFound ||
			searchError === SearchError.LanguageNotFound ||
			searchError === SearchError.EmptyLanguageParam ||
			searchError === SearchError.EmptyMarketParam ||
			searchError === SearchError.EmptyEditionParam ||
			(this.context.model &&
				this.context.model.hasDigitalManuals &&
				this.context.urlParameters &&
				this.context.urlParameters.edition &&
				!this.context.urlParameters.market)
		) {
			this.setState({
				isSearchOpen: true,
			});
			this.context.services.trackingPrepare.trackSearchError(
				searchError,
				this.context.model ? this.context.model.modelFamily : null,
				this.context.model ? this.context.model.modelType : null
			);
		}
	};

	handleVinError = (error: SearchError, vin: string) => {
		this.setState({
			isSearchOpen: true,
		});
		this.context.services.trackingPrepare.trackSearchError(
			error,
			this.context.model ? this.context.model.modelFamily : null,
			this.context.model ? this.context.model.modelType : null
		);
	};

	toggleSearch = () => {
		this.setState((prevState) => ({ isSearchOpen: !prevState.isSearchOpen }));
	};

	handleVinSubmit = async (vin: string, langauge: string) => {
		this.context.setTopicPilotData('', '', '');
		this.context.services.trackingPrepare.trackSearch(true, null, null, langauge, this.context.model.modelFamily, this.context.model.modelType);
		try {
			const manualsResult = await this.context.loadManuals('', '', null, langauge, vin);
			if (manualsResult.errorCode === ErrorCode.NoItems) {
				throw SearchError.NoManuals;
			}
			if (manualsResult.results[0].manualType === ManualType.Digital) {
				this.context.services.trackingPrepare.trackOpenTPManual(
					this.context.model ? this.context.model.modelFamily : '',
					this.context.model ? this.context.model.modelType : '',
					manualsResult.results[0].digitalManual.partNumber
				);
				this.context.setTopicPilotData(vin, manualsResult.results[0].digitalManual.partNumber, manualsResult.results[0].digitalManual.uiLanguage);
			} else {
				this.props.history.replace(`/Detail?manualLang=${langauge}&vin=${vin}`, {
					isCurrentModel: true,
				});
				if (manualsResult && manualsResult.results.length > 0) {
					this.context.services.trackingPrepare.trackSearchResult(
						manualsResult.results.length.toString(),
						true,
						this.context.model.modelFamily,
						this.context.model.modelType
					);
				}
			}
		} catch (error) {
			this.context.setError('Search_Error_Title', 'Search_Error_Description');
		}
	};

	handleFormSubmit = (productionPeriod: string, market: string, language: string, hasDigitalManuals: boolean, afterSubmit?: () => void) => {
		this.context.handleSearchDataSubmit(productionPeriod, market, language, hasDigitalManuals, this.props.history, afterSubmit);
	};

	goBackToHomepage = () => {
		this.context.clearUrlParameters();
		const redirectToDetail = () => {
			return this.props.history.push('/Models', {
				...this.props.history.location.state,
				isCurrentModel: !this.context.model || this.context.model.isFromVin || (!this.context.model.isOlder && !this.context.model.isFromVin),
				isScrolledToFamily: this.context.model && !this.context.model.isFromVin,
				isScrolledToVin: this.context.model && this.context.model.isFromVin,
			});
		};
		return redirectToDetail();
	};

	handleManualDownload = (manualModel: ManualModel) => {
		this.context.services.trackingPrepare.trackDownloadOnlineManual(
			this.context.model.modelFamily,
			this.context.model.modelType,
			manualModel.pdfManual.manualType === ManualPdfType.Manual,
			manualModel.pdfManual.id
		);

		window.open(manualModel.pdfManual.pdfFileUrl, '_blank');
	};

	render() {
		const mainManual = this.context.manuals.find((x) => x.pdfManual.manualFamily === ManualPdfType.Manual);
		return (
			<VinAppContext.Consumer>
				{(vinContext) => (
					<PageSection>
						<div className={`${styles['container-detailpage']}`}>
							{this.context.model && (
								<div id='container-track-scroll'>
									<Grid container direction='row' alignItems='center'>
										<Grid item xs={24} sm={12} className={styles['heading']}>
											<div className={styles['btn-back']}>
												<a
													onClick={(event) => {
														event.preventDefault();
														this.goBackToHomepage();
													}}
												>
													<ArrowBackIcon />
													<span>{this.context.services.resourceProvider.getText('Back_To_Models')}</span>
												</a>
											</div>
											<h1>{`${
												this.context.model
													? `${this.context.services.resourceProvider.getText(
															this.context.model.titleResourceKey
													  )} - ${this.context.services.resourceProvider.getText('Manuals', 'Manuals')}`
													: ''
											}`}</h1>
											{vinContext.vin && (
												<div className={styles['vin']}>
													{this.context.services.resourceProvider.getText('VIN_Code')}: <span>{vinContext.vin}</span>
												</div>
											)}
										</Grid>
										<Grid item xs={24} sm={12} className={styles['panel-search']}>
											<div>
												<span>{this.context.services.resourceProvider.getText('Search_Options')}</span>
												{this.state.isSearchOpen ? (
													<ExpandLessIcon onClick={this.toggleSearch} />
												) : (
													<ExpandMoreIcon onClick={this.toggleSearch} />
												)}
											</div>

											<Collapse in={this.state.isSearchOpen}>
												{this.context.model && !this.context.model.isFromVin && (
													<div className={styles['container-form']}>
														<SearchForm
															onSubmit={this.handleFormSubmit}
															formClassName={`${styles['form-search']}`}
															onProductionPeriodChange={() => {}}
															filterControlClassName={styles['form-search-control']}
															filterControllFilledClassName={styles['form-search-control-filled']}
															model={this.context.model}
															onError={this.handleCarSelectorError}
															isDetailPage={true}
														/>
													</div>
												)}
												{this.context.model && this.context.model.isFromVin && (
													<div className={styles['container-form-vin']}>
														<VinSearch onlyLanguage={true} onSubmit={this.handleVinSubmit} onError={this.handleVinError} />
													</div>
												)}
											</Collapse>
										</Grid>
										{mainManual && (
											<Grid item xs={24} sm={12} className={styles['panel-manual-main']}>
												<img
													src={
														mainManual.pdfManual.imageProxyPdfImage
															? mainManual.pdfManual.imageProxyPdfImage
															: mainManual.pdfManual.imageProxyImage
													}
													alt=''
												/>
												<div>
													<h3>{this.context.services.resourceProvider.getText('Car_manual_manual')}</h3>
													<p>{this.context.services.resourceProvider.getText('Car_manual_manual_description')}</p>
													<Fab
														color='primary'
														onClick={() => {
															this.handleManualDownload(mainManual);
														}}
														variant='extended'
													>
														{this.context.services.resourceProvider.getText('Download_Pdf')}
													</Fab>
												</div>
											</Grid>
										)}
									</Grid>
									{this.context.manuals.length > 0 && (
										<Grid container direction='row' alignItems='center'>
											<Grid item lg={12} className={styles['grid-manual']}>
												{this.context.manuals
													.filter((x) => x.pdfManual.manualType !== ManualPdfType.Manual)
													.map((x) => (
														<div className={`${styles['col-manual']}`} key={`manual-${x.pdfManual.id}`}>
															<img
																src={
																	x.pdfManual.imageProxyPdfImage
																		? x.pdfManual.imageProxyPdfImage
																		: x.pdfManual.imageProxyImage
																}
																alt=''
															/>
															<h3>
																{`${this.context.services.resourceProvider.getText(
																	`${x.pdfManual.manualType.toString()}_manual`
																)} - ${this.context.services.resourceProvider.getText(
																	`Manual_Model_${x.pdfManual.model.toString()}`
																)}`}
															</h3>
															<Fab
																color='secondary'
																onClick={() => {
																	this.handleManualDownload(x);
																}}
																variant='extended'
																className={`${styles['btn-download']}`}
															>
																{this.context.services.resourceProvider.getText('Download_Pdf')}
															</Fab>
														</div>
													))}
											</Grid>
										</Grid>
									)}
								</div>
							)}
						</div>
					</PageSection>
				)}
			</VinAppContext.Consumer>
		);
	}
}

export default DetailPage;
